import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Notice, describeAuthError } from './Notice';
import { isValidEmail, validatePassword } from './validators';
import type { Portal } from './types';
import { getLoginKitMessages } from './i18n';
import type { Locale } from './i18n';

export interface ForgotPasswordModalProps {
  portal: Portal;
  /** Idioma da interface (pt-BR, en, es). */
  locale?: Locale;
  onClose: () => void;
  /**
   * Passo 1 — solicita o envio do código de recuperação para o e-mail.
   * Se ausente, o envio é simulado.
   */
  onReset?: (email: string) => Promise<void> | void;
  /**
   * Passo 2 — verifica o código recebido e define a nova senha. Quando fornecido,
   * o modal exibe a etapa de código + nova senha (fluxo completo). Quando ausente,
   * cai no fluxo legado de confirmação ("verifique seu e-mail").
   */
  onResetVerify?: (params: { code: string; password: string }) => Promise<void> | void;
}

type Status = 'idle' | 'sending' | 'sent' | 'verify' | 'verifying' | 'done';

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Modal de recuperação de senha — completo. Com `onResetVerify`, percorre os dois
 * passos do reset do Clerk: (1) e-mail → envio do código; (2) código + nova senha →
 * sessão ativa. Sem ele, mantém o fluxo legado de confirmação. Fecha no Esc e no
 * clique do scrim. Compartilhado por todos os portais; reusa as classes do form.
 */
export function ForgotPasswordModal({ portal, locale = 'pt-BR', onClose, onReset, onResetVerify }: ForgotPasswordModalProps) {
  const t = getLoginKitMessages(locale);
  const minLen = portal.passwordMinLength ?? 6;
  const twoStep = Boolean(onResetVerify);

  const [status, setStatus] = useState<Status>('idle');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);

  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [pwError, setPwError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pwMessage = (key: ReturnType<typeof validatePassword>): string | null => {
    if (!key) return null;
    if (key === 'passwordMin') return t.validation.passwordMin.replace('{min}', String(minLen));
    return t.validation[key];
  };

  // Erros do passo 1 (request) e passo 2 (verify) são mapeados por situação.
  const describeRequestError = (err: unknown): string => {
    const msg = err instanceof Error ? err.message : '';
    if (msg.includes('identifier_not_found') || msg.includes('not_found')) return t.modal.emailNotFound;
    return describeAuthError(err, t.notice, t.modal.error);
  };
  const describeVerifyError = (err: unknown): string => {
    const msg = err instanceof Error ? err.message : '';
    if (msg.includes('code') || msg.includes('expired') || msg.includes('verification')) return t.modal.codeInvalid;
    if (msg.includes('password') || msg.includes('pwned')) return t.modal.weakPassword;
    return describeAuthError(err, t.notice, t.modal.error);
  };

  // Passo 1 — solicita o código.
  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!email.trim()) {
      setEmailError(t.validation.required);
      return;
    }
    if (!isValidEmail(email)) {
      setEmailError(t.validation.email);
      return;
    }
    setEmailError(null);
    setError(null);
    setStatus('sending');
    try {
      if (onReset) await onReset(email);
      else await wait(1200);
      setStatus(twoStep ? 'verify' : 'sent');
    } catch (err) {
      setStatus('idle');
      setError(describeRequestError(err));
    }
  };

  // Passo 2 — verifica o código e define a nova senha.
  const verifyReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'verifying' || !onResetVerify) return;

    let invalid = false;
    if (!code.trim()) {
      setCodeError(t.modal.codeRequired);
      invalid = true;
    } else {
      setCodeError(null);
    }
    const pwKey = validatePassword(password, minLen);
    if (pwKey) {
      setPwError(pwMessage(pwKey));
      invalid = true;
    } else {
      setPwError(null);
    }
    if (invalid) return;

    setError(null);
    setStatus('verifying');
    try {
      await onResetVerify({ code: code.trim(), password });
      setStatus('done');
    } catch (err) {
      setStatus('verify');
      setError(describeVerifyError(err));
    }
  };

  const resendCode = async () => {
    setError(null);
    setCodeError(null);
    try {
      if (onReset) await onReset(email);
    } catch (err) {
      setError(describeRequestError(err));
    }
  };

  const modalStyle = { '--accent': portal.accent, '--accent-soft': portal.accentSoft } as CSSProperties;

  let body: React.ReactNode;
  if (status === 'sent') {
    // Fluxo legado (sem onResetVerify): apenas confirma o envio.
    body = (
      <div className="modal-body modal-confirm" key="sent">
        <span className="confirm-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M4 7l8 6 8-6" />
            <rect x="4" y="5" width="16" height="14" rx="2" />
          </svg>
        </span>
        <h2>{t.modal.sentTitle}</h2>
        <p>
          {t.modal.sentPrefix} <strong>{email}</strong>, {t.modal.sentSuffix}
        </p>
        <button type="button" className="primary-btn" onClick={onClose}>
          <span className="btn-label">{t.modal.understood}</span>
        </button>
        <button type="button" className="modal-resend" onClick={() => setStatus('idle')}>
          {t.modal.tryAnotherEmail}
        </button>
      </div>
    );
  } else if (status === 'done') {
    body = (
      <div className="modal-body modal-confirm" key="done">
        <span className="confirm-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M5 12l4 4 10-10" />
          </svg>
        </span>
        <h2>{t.modal.resetDoneTitle}</h2>
        <p>{t.modal.resetDoneText}</p>
        <button type="button" className="primary-btn" onClick={onClose}>
          <span className="btn-label">{t.modal.resetDoneCta}</span>
        </button>
      </div>
    );
  } else if (status === 'verify' || status === 'verifying') {
    const [leadPre, leadPost] = t.modal.verifyLead.split('{email}');
    body = (
      <form className="modal-body" onSubmit={verifyReset} key="verify" noValidate>
        <div className="modal-eyebrow">{portal.name}</div>
        <h2 className="modal-title">{t.modal.verifyTitle}</h2>
        <p className="modal-lead">
          {leadPre}
          <strong>{email}</strong>
          {leadPost}
        </p>
        {error && (
          <Notice tone="error" title={t.notice.errorTitle} onDismiss={() => setError(null)} dismissLabel={t.notice.dismiss}>
            {error}
          </Notice>
        )}

        <label className="field">
          <span className="field-label">{t.modal.codeLabel}</span>
          <input
            className="field-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            placeholder={t.modal.codePlaceholder}
            value={code}
            aria-invalid={codeError ? true : undefined}
            aria-describedby={codeError ? 'reset-code-error' : undefined}
            onChange={(e) => {
              setCode(e.target.value);
              if (codeError) setCodeError(null);
            }}
          />
          {codeError && (
            <span className="field-error" id="reset-code-error" role="alert">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7.5v5" />
                <path d="M12 16h.01" />
              </svg>
              {codeError}
            </span>
          )}
        </label>

        <label className="field">
          <span className="field-label">{t.modal.newPasswordLabel}</span>
          <span className="field-pw">
            <input
              className="field-input"
              type={showPw ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••"
              value={password}
              aria-invalid={pwError ? true : undefined}
              aria-describedby={pwError ? 'reset-pw-error' : undefined}
              onChange={(e) => {
                setPassword(e.target.value);
                if (pwError) setPwError(pwMessage(validatePassword(e.target.value, minLen)));
              }}
            />
            <button
              type="button"
              className="pw-toggle"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? t.form.hidePassword : t.form.showPassword}
            >
              {showPw ? t.form.hidePassword : t.form.showPassword}
            </button>
          </span>
          {pwError && (
            <span className="field-error" id="reset-pw-error" role="alert">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7.5v5" />
                <path d="M12 16h.01" />
              </svg>
              {pwError}
            </span>
          )}
        </label>

        <button type="submit" className={`primary-btn ${status === 'verifying' ? 'is-loading' : ''}`}>
          <span className="btn-label">{status === 'verifying' ? t.modal.verifySubmitting : t.modal.verifySubmit}</span>
        </button>

        <p className="modal-help">
          <button type="button" className="modal-link" onClick={() => void resendCode()}>
            {t.modal.resendCode}
          </button>
          {' · '}
          <button type="button" className="modal-link" onClick={onClose}>
            {t.modal.backToLogin}
          </button>
        </p>
      </form>
    );
  } else {
    // idle / sending — passo 1: e-mail.
    body = (
      <form className="modal-body" onSubmit={requestCode} key="form" noValidate>
        <div className="modal-eyebrow">{portal.name}</div>
        <h2 className="modal-title">{t.modal.title}</h2>
        <p className="modal-lead">{t.modal.lead}</p>
        {error && (
          <Notice tone="error" title={t.notice.errorTitle} onDismiss={() => setError(null)} dismissLabel={t.notice.dismiss}>
            {error}
          </Notice>
        )}
        <label className="field">
          <span className="field-label">{t.modal.identifierLabel}</span>
          <input
            className="field-input"
            type="email"
            autoFocus
            placeholder={portal.identifier.placeholder}
            value={email}
            aria-invalid={emailError ? true : undefined}
            aria-describedby={emailError ? 'forgot-email-error' : undefined}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(null);
            }}
            onBlur={(e) => {
              const value = e.target.value;
              if (value.trim() && !isValidEmail(value)) setEmailError(t.validation.email);
            }}
          />
          {emailError && (
            <span className="field-error" id="forgot-email-error" role="alert">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7.5v5" />
                <path d="M12 16h.01" />
              </svg>
              {emailError}
            </span>
          )}
        </label>
        <button type="submit" className={`primary-btn ${status === 'sending' ? 'is-loading' : ''}`}>
          <span className="btn-label">{status === 'sending' ? t.modal.sending : t.modal.sendRecoveryLink}</span>
        </button>
        <p className="modal-help">
          {t.modal.rememberedPassword}{' '}
          <button type="button" className="modal-link" onClick={onClose}>
            {t.modal.backToLogin}
          </button>
        </p>
      </form>
    );
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={modalStyle} role="dialog" aria-modal="true">
        <button type="button" className="modal-close" onClick={onClose} aria-label={t.modal.close}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        {body}
      </div>
    </div>
  );
}
